<template>
    <div class="mb-10">
        <Head title="Pending Challenge Videos" />

        <heading>
            <template #heading>
                <div class="flex items-center">
                    <Link :href="post.showUrl" class="-m-px p-2 hover:text-blue-500 focus:text-blue-600 hover:bg-white focus:bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                        </svg>
                    </Link>
                    <span class="ml-2">Pending Challenge Videos</span>
                </div>
            </template>
        </heading>

        <div class="lg:grid lg:gap-4 lg:grid-cols-12" v-if="requests && requests.length">
            <div class="flex flex-col space-y-4 lg:col-span-8 lg:px-0">
                <div class="grid gap-y-3 grid-cols-1 sm:gap-x-2 sm:gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                    <div v-for="(request, i) in requests" class="relative flex flex-col shadow overflow-hidden lg:border-0 lg:border-gray-300 lg:rounded-lg lg:shadow-none">
                        <div class="absolute z-10 pt-3 pl-3 flex w-full gap-1">
                            <div v-if="request.verificationPhotoUrl" class="flex-shrink-0 w-20 h-20 rounded-full shadow-md overflow-hidden ring-2 ring-white">
                                <img :src="request.verificationPhotoUrl" alt="Verification Photo" class="w-full h-full object-cover object-center" />
                            </div>
                            <div class="flex-grow px-5 py-2">
                                <link-button :href="route('posts.challengeRequests.show', { post: post, competitor: request.id })" class="w-full shadow-md sm:ring-0 sm:ring-offset-0">Review</link-button>
                            </div>
                        </div>
                        <div class="aspect-w-9 aspect-h-16 bg-gray-200">
                            <img :src="currentFrame[i]" class="w-full h-full object-cover object-center" alt="" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Aside -->
            <div class="hidden lg:block lg:col-span-4 pr-6">
                <div class="sticky top-8 space-y-4">
                    <div class="px-4 py-6 bg-white rounded-lg shadow">
                        <div class="block hover:opacity-75 space-y-4">
                            <div class="flex flex-col justify-center align-middle text-center">
                                <div class="flex justify-center mb-1 mr-1 text-pink-500 text-6xl font-bold">
                                    <span class="currency font-display">{{ post.pledgedAmount | abbr }}</span>
                                </div>
                                <div class="text-pink-300 text-xs font-semibold uppercase">value of tokens pledged</div>
                            </div>

                            <!-- Supporter Stats -->
                            <div class="grid gap-1 grid-cols-3 text-center">
                                <div class="col-span-1">
                                    {{ post.pledgesCount | abbr }}
                                    <div class="text-gray-500 text-xs uppercase">
                                        {{ "pledge" | pluralize(post.pledgesCount) }}
                                    </div>
                                </div>
                                <div class="col-span-1">
                                    {{ post.likesCount | abbr }}
                                    <div class="text-gray-500 text-xs uppercase">{{ "like" | pluralize(post.likesCount) }}</div>
                                </div>
                                <div class="col-span-1">
                                    {{ post.commentsCount | abbr }}
                                    <div class="text-gray-500 text-xs uppercase">
                                        {{ "comment" | pluralize(post.commentsCount) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Link :href="post.showUrl" class="group block bg-white rounded-lg shadow space-y-4">
                        <!-- Featured Image -->
                        <div class="aspect-w-10 aspect-h-7 block p-0 w-full h-48 shadow-inner group-hover:opacity-75 sm:rounded-t-lg sm:overflow-hidden" v-html="post.featuredImageHtml"></div>

                        <!-- Post Details -->
                        <div class="pb-3 px-4">
                            <div class="line-clamp-3 group-hover:text-gray-500 text-gray-700 text-sm" v-html="post.html"></div>

                            <button class="flex items-center pt-3 group-hover:text-blue-500 text-gray-500 text-sm">
                                <svg class="-ml-1 mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                </svg>
                                <span>Go to challenge</span>
                            </button>
                        </div>
                    </Link>
                </div>
            </div>
        </div>

        <!-- Display the no-results component when there are no competitors -->
        <div class="bg-white shadow overflow-hidden lg:rounded-lg" v-else>
            <no-results :show="!requests.length">
                <span class="capitalize">No Pending Challenge Videos</span>
            </no-results>
        </div>
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout"
import AppHead from "@/Components/AppHead"
import AppLink from "@/Components/AppLink"
import Heading from "@/Components/Heading"
import Tooltip from "@/Components/Tooltip"
import JetButton from "@/Jetstream/Button"
import LinkButton from "@/Components/Buttons/LinkButton"
import NoResults from "@/Jetstream/NoResults"

export default {
    components: { NoResults, LinkButton, AppLink, Tooltip, Heading, AppHead, JetButton },
    layout: AppLayout,

    data() {
        return {
            currentFrameIndex: {},
            currentFrame: {},
            timelapseInterval: null,
        }
    },

    created() {
        this.requests.forEach((request, i) => {
            this.$set(this.currentFrameIndex, i, 0)
            this.$set(this.currentFrame, i, request.video.frames[0])
        })

        this.startTimelapse()
    },

    beforeDestroy() {
        clearInterval(this.timelapseInterval)
    },

    methods: {
        startTimelapse() {
            this.timelapseInterval = setInterval(() => {
                this.requests.forEach((request, i) => {
                    this.currentFrameIndex[i] = (this.currentFrameIndex[i] + 1) % request.video.frames.length
                    this.$set(this.currentFrame, i, request.video.frames[this.currentFrameIndex[i]])
                })
            }, 500) // Adjust interval to control speed of timelapse
        },
    },

    props: {
        post: {
            required: true,
            type: Object,
        },
    },

    computed: {
        requests() {
            return this.post.pendingCompetitors
        },
    },
}
</script>
